import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogFooter,
	DialogTop
} from '@/components/DialogSheet';
import { useShallowEqualSelector } from '@/hooks';
import { useIsOwner, useIsTrialing } from '@/hooks/user';
import Share from '@/icons/share.svg';
import Limited1x from '@/images/limited/img-mobile-1x.png';
import Limited2x from '@/images/limited/img-mobile-2x.png';
import { BILLING_TAB } from '@/pages/Account/constants';
import routes from '@/routes';
import {
	setIsModalUpgradePlanOpen,
	setModalUpgradePlanScope
} from '@/store/profile';
import { delay } from '@/utils';

function ModalUpgradePlan() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const isTrialing = useIsTrialing();
	const { t, i18n } = useTranslation();

	const { open, scope: scopeState } = useShallowEqualSelector(state => ({
		open: state.profile.isModalUpgradePlanOpen,
		scope: state.profile.modalUpgradePlanScope
	}));
	const isOwner = useIsOwner();
	const availableScopeTranslations = i18n.exists(`upgradePlan.${scopeState}`);

	const scope = (availableScopeTranslations && scopeState) || 'share';

	const handleClose = async () => {
		dispatch(setIsModalUpgradePlanOpen(false));
		await delay(200);
		dispatch(setModalUpgradePlanScope(null));
	};

	const handleClick = () => {
		if (scope === 'share') {
			navigate(routes.checkout);
		} else {
			navigate(routes.myAccount, {
				state: { openChangePlanModal: isTrialing, currentTab: BILLING_TAB }
			});
		}

		handleClose();
	};

	useEffect(
		() => () => {
			handleClose();
		},
		[]
	);

	const contentGuest = (
		<>
			<DialogTop
				className="font-sans text-sm leading-6"
				description={t(`upgradePlan.guest.description`)}
				title={t(`upgradePlan.guest.title`)}
			/>
			<div className="w-full p-4 md:w-[397px]">
				<div className="rounded-lg bg-red-background p-4">
					<p className="text-xs leading-5 md:text-sm">
						{t(`upgradePlan.guest.description`)}
					</p>
				</div>
			</div>
		</>
	);

	const content = (
		<>
			<DialogTop
				className="font-sans text-sm leading-6"
				description={t(`upgradePlan.${scope}.description`)}
				title={t(`upgradePlan.${scope}.title`)}
			/>
			<div className="w-full p-4 md:w-[397px]">
				<div className="flex flex-col gap-4 rounded-lg bg-red-background px-4 md:flex-row md:items-end md:gap-2 md:px-5">
					<div className="flex flex-col gap-2 pt-6 md:py-6">
						<Share aria-hidden className="size-6 text-red" />
						<p className="text-xs font-bold leading-5">
							{t(`upgradePlan.${scope}.description`)}
						</p>
						<p className="text-xs leading-5">
							{t(`upgradePlan.${scope}.content`)}
						</p>
					</div>
					<img
						alt={t('myDocuments.limitedPlan.title')}
						src={Limited1x}
						srcSet={`${Limited1x} 1x, ${Limited2x} 2x`}
						width={130}
						height={131}
					/>
				</div>
			</div>
			<DialogFooter className="flex w-full flex-col">
				<Button
					className="w-full leading-6"
					onClick={handleClick}
					type="submit"
				>
					{t(`upgradePlan.${scope}.buttonCTA`)}
				</Button>
				<Button
					className="w-full leading-6"
					onClick={handleClose}
					variant="outline"
				>
					{t('common.cancel')}
				</Button>
			</DialogFooter>
		</>
	);

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent
				className="md:w-[397px]"
				onClose={handleClose}
				onOpenAutoFocus={event => event.preventDefault()}
			>
				{isOwner ? content : contentGuest}
			</DialogContent>
		</Dialog>
	);
}

export default ModalUpgradePlan;
